<template>
	<form
		class="cart-footer-frame"
		:class="{ active: isActive }"
		@submit.prevent=""
	>
		<div v-if="isPickUpTimeAvailable" class="cart-footer-container">
			<div class="cart-footer-block">
				<span class="cart-footer-label">Pickup time</span>
				<pick-up-time-picker
					:pick-up-time="pickUpTime"
					:pick-up-time-from="currentPickUpTimeFrom"
					:pick-up-time-to="currentPickUpTimeTo"
					:is-pick-up-time-valid="isPickUpTimeValid"
					:show-pick-up-time-picker="showPickUpTimePicker"
					:has-error="!isPickUpTimeValid"
					@change-time="changePickUpTime"
				/>
			</div>
			<div class="cart-footer-block">
				<t-phone-input
					:value="currentPhoneNumber"
					:error="isPhoneErrorShown ? phoneNumberError : ''"
					:error-icon-visible="false"
					cleanable
					menu-style
					@blur="changePhone"
					@clean="currentPhoneNumber = ''"
				/>
			</div>

			<div
				v-if="isEnforceCollectEmailAndPhoneEnabled"
				class="cart-footer-block"
			>
				<t-input
					debounced
					:value="currentEmail"
					label="Email"
					cleanable
					:error="emailError"
					:error-icon-visible="false"
					menu-style
					required
					@input="changeEmail"
					@clean="currentEmail = ''"
				/>
			</div>
			<div class="cart-footer-block">
				<t-input
					debounced
					:value="currentCustomerName"
					:max-length="50"
					label="Name"
					:cleanable="!isTabOpened"
					:readonly="isTabOpened"
					menu-style
					required
					:error="!isCustomerNameValid ? 'Name required' : ''"
					:error-icon-visible="false"
					@input="changeCustomerNameToPrint"
					@clean="currentCustomerName = ''"
				/>
			</div>
			<div class="cart-footer-block d-flex flex-align-center">
				<custom-check-box
					v-if="isUtensilsVisible"
					id="pick-up-utensils"
					class="cart-footer-utensils"
					:disabled="!isTabEmpty && isTabOpened"
					:label="taxSettings.utensilsText || 'Utensils'"
					:checked="includeUtensils"
					@change="changeUtensils"
				/>
			</div>
		</div>

		<div class="cart-footer-buttons-holder">
			<primary-button
				:disabled="isButtonsDisabled"
				:loading="isLoading"
				large
				@click="checkout"
			>
				<template>
					{{ isTabOpened ? 'Update' : 'Checkout' }}
					<template v-if="totalProductsCount">
						<span class="ml-8 mr-8">-</span>
						<formatted-price :price="totalPrice" decimal-height="70" />
					</template>
				</template>
			</primary-button>
		</div>
		<portal v-if="isActive" to="modal">
			<transition name="bottom-slide">
				<credit-card-modal
					v-if="isShowCreditCard"
					:amount="totalPrice"
					:is-pick-up="true"
					@set-is-pick-up-valid="setIsPickUpValid"
					@credit-card-added="creditCardAdded"
					@close="hideCreditCardModal"
				/>
			</transition>
			<transition name="fade">
				<payment-notice-modal
					v-if="isPaymentNoticeModalVisible"
					@close="hidePaymentNoticeModal"
				/>
			</transition>
			<transition name="fade">
				<unavailability-modal
					v-if="isUnAvailableModalOpen"
					v-bind="unAvailableModalProps"
					@on-action="onModalAction"
					@close="closeUnAvailableModal"
				/>
			</transition>
			<transition name="fade">
				<mandatory-order-requirements-modal
					v-if="isMandatoryModalOpen"
					dark-theme
					:condition="mandatoryCondition"
					@close="isMandatoryModalOpen = false"
				/>
			</transition>
		</portal>
	</form>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { debounce } from 'lodash'

import CustomCheckBox from '@/components/forms/CustomCheckBox'
import PickUpTimePicker from './PickUpTimePicker'
import CreditCardModal from '@/components/modals/CreditCardModal'
import PaymentNoticeModal from '@/components/modals/PaymentNoticeModal'
import UnavailabilityModal from '@/components/modals/UnavailabilityModal.vue'
import MandatoryOrderRequirementsModal from '@/components/modals/MandatoryOrderRequirementsModal.vue'
import PrimaryButton from '@/components/Buttons/PrimaryButton.vue'
import TInput from '@/components/Ticketing/Controls/TInput.vue'
import TPhoneInput from '@/components/Ticketing/Controls/TPhoneInput.vue'
import FormattedPrice from '@/components/FormattedPrice.vue'

import pickUpValidation from '@/mixins/pickUpValidation'
import routerMixin from '@/mixins/routerMixin'

import storage from '@/utils/storage'
import { emailValidation, scrollToBottom } from '@/utils/helpers'
import { VueMaskFilter } from 'v-mask'

import {
	ORDER_LIMITATION_CONDITION,
	MIN_ORDER_AMOUNT,
	DEFAULT_UNAVAILABLE_MODAL_PROPS,
	ALL_ORDER_TYPES_NAMES,
	ORDERS_ACTIONS_TEXT_LIST
} from '@/constants'

export default {
	name: 'CartFooterPickUp',

	mixins: [pickUpValidation, routerMixin],

	components: {
		TPhoneInput,
		TInput,
		PrimaryButton,
		UnavailabilityModal,
		PickUpTimePicker,
		CustomCheckBox,
		CreditCardModal,
		PaymentNoticeModal,
		MandatoryOrderRequirementsModal,
		FormattedPrice
	},

	props: {
		isActive: {
			type: Boolean,
			default: false
		},
		isLoading: {
			type: Boolean,
			default: false
		},
		beforeCheckout: {
			type: Function,
			default: () => {}
		}
	},

	data() {
		return {
			currentPickUpTimeFrom: '',
			currentPickUpTimeTo: '',
			currentCustomerName: '',
			currentPhoneNumber: '',
			currentIncludeUtensils: '',
			currentEmail: '',
			isProductsValid: true,
			showPickUpTimePicker: 0,
			isShowCreditCard: false,
			isPaymentNoticeModalVisible: false,
			isPickUpTimeValid: true,
			phoneNumberError: '',
			isPhoneErrorShown: false,
			isEmailValid: true,
			isCustomerNameValid: true,
			isUnAvailableModalOpen: false,
			unAvailableModalProps: { ...DEFAULT_UNAVAILABLE_MODAL_PROPS },
			isMandatoryModalOpen: false,
			mandatoryCondition: ''
		}
	},

	computed: {
		...mapState('venue', [
			'venueInfo',
			'pickUpHours',
			'isPickUpOrderingAvailable',
			'pickUpTime',
			'settings'
		]),
		...mapGetters('venue', [
			'isPickUpTimeAvailable',
			'isWebVersionAllowASAPOption'
		]),
		...mapState('cart', [
			'creditCardId',
			'email',
			'customerNameToPrint',
			'isInVenue',
			'isPickUp',
			'pickupTimeFrom',
			'pickupTimeTo',
			'phoneNumber',
			'includeUtensils',
			'isCartRecreated'
		]),
		...mapState('cart', { cartId: 'id' }),
		...mapGetters('cart', [
			'totalPrice',
			'totalProductsCount',
			'cartHasFood',
			'cartHasBeverage',
			'isTabOpened',
			'isTabEmpty'
		]),

		emailError() {
			if (!this.isEmailValid) {
				return this.currentEmail ? 'Check your email' : 'Email required'
			}

			return ''
		},

		timeZone() {
			return this.venueInfo.timeZone
		},

		venueSettings() {
			return this.venueInfo.settings
		},

		taxSettings() {
			return this.venueSettings.taxSettings
		},

		isEnforceCollectEmailAndPhoneEnabled() {
			return this.venueSettings.isEnforceCollectEmailAndPhoneEnabled
		},

		isUtensilsVisible() {
			const { utensilsForPickUp } = this.venueSettings.taxSettings

			return utensilsForPickUp && this.cartHasFood
		},

		isButtonsDisabled() {
			return !this.totalProductsCount || this.isLoading
		}
	},

	watch: {
		isActive(val) {
			if (val) {
				this.init()
			}
		},
		isShowCreditCard(val) {
			if (!val) {
				this.$emit('update-parent-button')
			}
		},
		isLoading(val) {
			this.unAvailableModalProps = {
				...this.unAvailableModalProps,
				isLoading: val
			}
		}
	},

	created() {
		this.init()
	},

	methods: {
		...mapActions('venue', ['setAllOrderingAvailable']),
		...mapActions('cart', [
			'getCart',
			'updateCart',
			'openTab',
			'updateTab',
			'setCartInfoMessage',
			'toggleIsCartRecreated'
		]),
		...mapActions('ui', ['addMainError']),

		init() {
			const isPickUpTimeValid = this.pickUpValidation(
				this.pickupTimeFrom,
				this.pickupTimeTo
			)

			this.currentPickUpTimeFrom = isPickUpTimeValid
				? this.pickupTimeFrom
				: null
			this.currentPickUpTimeTo = isPickUpTimeValid ? this.pickupTimeTo : null

			this.currentCustomerName =
				this.customerNameToPrint || storage.getStorage('name')
			this.currentPhoneNumber = this.getClearPhoneNumber()
			this.currentIncludeUtensils = this.includeUtensils

			if (this.isEnforceCollectEmailAndPhoneEnabled) {
				this.currentEmail = this.email || storage.getStorage('email')
			}

			const isValid = this.validation({
				isSilent: true
			})

			if (isValid) {
				this.callUpdateCart()
			} else {
				this.$emit('expand-footer')
			}
		},

		getClearPhoneNumber() {
			const phoneNumber =
				this.phoneNumber?.replace('+1', '') || storage.getStorage('phone')

			return phoneNumber || ''
		},

		showCreditCardModal() {
			this.isShowCreditCard = true
		},

		hideCreditCardModal() {
			this.isShowCreditCard = false
		},

		showPaymentNoticeModal() {
			this.isPaymentNoticeModalVisible = true
		},

		hidePaymentNoticeModal() {
			this.isPaymentNoticeModalVisible = false
		},

		changePickUpTime({ pickUpTimeFrom, pickUpTimeTo }) {
			this.currentPickUpTimeFrom = pickUpTimeFrom
			this.currentPickUpTimeTo = pickUpTimeTo

			this.isPickUpTimeValid = this.pickUpValidation(
				this.currentPickUpTimeFrom,
				this.currentPickUpTimeTo
			)

			this.callUpdateCart({
				pickupTimeFrom: this.currentPickUpTimeFrom,
				pickupTimeTo: this.currentPickUpTimeTo,
				isNeedShowExpiredPopup: true
			})
		},

		changeEmail(email) {
			if (this.currentEmail === email?.trim()) return

			this.currentEmail = email && email.trim()
			storage.setStorage('email', email)

			const isValid = this.emailValidation()

			if (isValid) {
				this.callDeferredUpdateCart()
				this.isEmailValid = true
			}
		},

		changePhone(phoneNumber) {
			if (phoneNumber === this.currentPhoneNumber) return

			this.currentPhoneNumber = phoneNumber
			storage.setStorage('phone', phoneNumber)

			this.validatePhone(phoneNumber)

			if (this.phoneNumberError) return

			this.callDeferredUpdateCart()
		},

		changeCustomerNameToPrint(name) {
			if (this.currentCustomerName === name?.trim()) return

			this.currentCustomerName = name && name.trim()
			storage.setStorage('name', this.currentCustomerName)

			const isValid = this.customerNameValidation()

			if (isValid) {
				this.callDeferredUpdateCart()
				this.isCustomerNameValid = true
			}
		},

		changeUtensils(includeUtensils) {
			this.currentIncludeUtensils = includeUtensils
			this.callUpdateCart({ includeUtensils, isNeedShowExpiredPopup: true })
		},

		async creditCardAdded(creditCard) {
			this.setIsLoading(true)

			await this.getCart(true)

			if (this.isCartRecreated) {
				this.setIsLoading(false)

				return
			}

			await this.callUpdateCart({ creditCardId: creditCard.id })

			await this.callOpenTab()

			this.setIsLoading(false)
		},

		callDeferredUpdateCart: debounce(async function(params) {
			await this.callUpdateCart({ ...params, isNeedShowExpiredPopup: true })
		}, 300),

		async callUpdateCart(params = {}) {
			let payload = {
				customerNameToPrint:
					this.currentCustomerName || this.customerNameToPrint,
				email: this.currentEmail || this.email,
				includeUtensils: this.currentIncludeUtensils,
				phoneNumber: this.getPhoneForRequest(this.currentPhoneNumber),
				...params
			}

			if ('pickupTimeFrom' in payload && this.isWebVersionAllowASAPOption) {
				if (this.currentPickUpTimeFrom === 'ASAP') {
					payload = {
						...payload,
						isAsap: true,
						pickupTimeFrom: null,
						pickupTimeTo: null
					}
				} else {
					payload = {
						...payload,
						isAsap: false,
						pickupTimeFrom: this.currentPickUpTimeFrom,
						pickupTimeTo: this.currentPickUpTimeTo
					}
				}
			}

			try {
				await this.updateCart(payload)
			} catch (e) {}
		},

		getCurrentTypeAvailableHours() {
			return this.pickUpHours?.length && this.pickUpHours[0]
		},

		checkAvailability() {
			let isAvailable = true
			this.unAvailableModalProps = { ...DEFAULT_UNAVAILABLE_MODAL_PROPS }

			if (this.isPickUpOrderingAvailable) {
				return isAvailable
			}

			this.unAvailableModalProps = {
				...DEFAULT_UNAVAILABLE_MODAL_PROPS,
				actionText: ORDERS_ACTIONS_TEXT_LIST.cancel,
				action: this.deleteOrder,
				orderType: ALL_ORDER_TYPES_NAMES.pickUp,
				availablePeriod: this.getCurrentTypeAvailableHours()
			}

			isAvailable = false

			return isAvailable
		},

		deleteOrder() {
			this.$emit('delete-order')
		},

		onModalAction(action) {
			action()
		},

		closeUnAvailableModal() {
			this.isUnAvailableModalOpen = false
		},

		async checkout() {
			if (this.totalPrice < MIN_ORDER_AMOUNT) {
				return this.showPaymentNoticeModal()
			}

			const isValid = this.validation({
				isSilent: false
			})

			if (!isValid) {
				scrollToBottom()

				return
			}

			try {
				this.setIsLoading(true)
				await this.beforeCheckout()
			} catch (e) {
				this.setIsLoading(false)
			}

			const storeCartId = storage.getStorage('cartId')

			await this.getCart()

			if (this.cartId && storeCartId && this.cartId !== storeCartId) {
				this.toggleIsCartRecreated()
				this.setIsLoading(false)

				return
			}

			const isAvailable = this.checkAvailability()

			if (!isAvailable) {
				this.setIsLoading(false)
				this.isUnAvailableModalOpen = true

				return
			}

			try {
				await this.callOpenTab()
			} catch (e) {
				this.setIsLoading(false)
			}
		},

		async callOpenTab() {
			try {
				this.setIsLoading(true)

				await this.getCart()

				if (this.isCartRecreated) {
					this.setIsLoading(false)
					return
				}

				if (!this.creditCardId) {
					this.setIsLoading(false)
					this.showCreditCardModal()
					return
				}

				if (this.isTabOpened) {
					await this.updateTab({
						cartId: this.cartId
					})
				} else {
					await this.openTab({
						cartId: this.cartId
					})
				}
			} catch (e) {
				this.setIsLoading(false)
			}
		},

		validation({ isSilent }) {
			this.setAllOrderingAvailable()

			const isProductsValid = this.productsValidation()

			if (!isProductsValid) {
				this.isMandatoryModalOpen = true

				return
			}

			const isPickUpTimeValid = this.pickUpValidation(
				this.currentPickUpTimeFrom,
				this.currentPickUpTimeTo
			)

			const isCustomerNameValid = this.customerNameValidation()
			let isEmailValid = true

			if (this.isEnforceCollectEmailAndPhoneEnabled) {
				isEmailValid = this.emailValidation()
			}

			if (!isSilent) {
				this.isProductsValid = isProductsValid
				this.isPickUpTimeValid = isPickUpTimeValid
				this.isCustomerNameValid = isCustomerNameValid
				this.validatePhone(this.currentPhoneNumber, true)

				if (this.isEnforceCollectEmailAndPhoneEnabled) {
					this.isEmailValid = isEmailValid
				}
			} else {
				this.validatePhone(this.currentPhoneNumber)
			}

			if (this.phoneNumberError) return false

			return (
				isProductsValid &&
				isPickUpTimeValid &&
				isCustomerNameValid &&
				isEmailValid
			)
		},

		getFormattedPhone(phone) {
			return VueMaskFilter(phone, '#'.repeat(13))
		},
		validatePhone(maskedPhone, isPhoneErrorShown = false) {
			this.phoneNumberError = ''
			const phone = this.getFormattedPhone(maskedPhone)
			this.isPhoneErrorShown = isPhoneErrorShown
			if (!phone) {
				this.phoneNumberError = `Phone required`
				return
			}
			if (
				phone.length < 10 ||
				phone.length > 12 ||
				(phone.length !== 11 && phone[0] === '1')
			) {
				this.phoneNumberError = `Check your phone number`
			}
		},
		getPhoneForRequest(phone) {
			if (!phone) return ''
			const formattedPhone = this.getFormattedPhone(phone)
			return formattedPhone.length === 10
				? `+1${formattedPhone}`
				: `+${formattedPhone}`
		},

		productsValidation() {
			const { orderLimCondition, orderLimPickUp } = this.venueInfo.settings
			let message = null
			let isProductsValid = true

			if (orderLimPickUp) {
				switch (orderLimCondition) {
					case ORDER_LIMITATION_CONDITION.ORDER_HAS_AT_LIST_ONE_FOOD:
						if (!this.cartHasFood) {
							this.mandatoryCondition = 'At least 1 food item'

							message = {
								title: 'Your order must include',
								subTitle: this.mandatoryCondition
							}
							isProductsValid = false
						}
						break
					case ORDER_LIMITATION_CONDITION.ORDER_HAS_AT_LIST_ONE_DRINK:
						if (!this.cartHasBeverage) {
							this.mandatoryCondition = 'At least 1 drink item'

							message = {
								title: 'Your order must include',
								subTitle: this.mandatoryCondition
							}
							isProductsValid = false
						}
						break
					default:
						break
				}
			}

			this.setCartInfoMessage(message)
			return isProductsValid
		},

		phoneNumberValidation() {
			return this.currentPhoneNumber && this.currentPhoneNumber.length === 10
		},

		emailValidation() {
			return emailValidation(this.currentEmail)
		},

		customerNameValidation() {
			return !!this.currentCustomerName && !!this.currentCustomerName.length
		},

		setIsPickUpValid(val) {
			this.isPickUpTimeValid = val
		},

		setIsLoading(val) {
			this.$emit('set-is-loading', val)
		}
	}
}
</script>
